/** mercury-bithumb midnight paper — via mercury-analyser-ibkr bridge. */

export type BithumbPaperTrade = {
  base: string;
  market?: string;
  board?: number;
  entry: number;
  entered_at: string;
  exited_at?: string;
  exit?: number;
  reason?: string;
  net?: number;
  hold_m?: number;
  board_pct?: number;
  mom5?: number;
  q1h?: number;
  tier?: 'core' | 'sat' | string;
  stack?: string;
  mark?: number;
  unreal_net?: number;
  age_m?: number;
};

export type BithumbWatchRow = {
  base: string;
  market?: string;
  alert_at?: string;
  alert_at_kst?: string;
  segment_pct?: number;
  n_alerts?: number;
  last?: number | null;
  board?: number | null;
  board_pct?: number | null;
  mom5?: number | null;
  q1h?: number | null;
  tier?: string | null;
  allowlisted?: boolean;
  overshot?: boolean;
  n_ticks_60m?: number;
  in_entry_window?: boolean;
  status: string;
  ready?: boolean;
};

export type BithumbClusterInfo = {
  label: string;
  n_alerts: number;
  n_symbols: number;
  t0_kst?: string | null;
  t1_kst?: string | null;
  symbols?: string[];
  allowlisted?: string[];
  live?: boolean;
};

export type BithumbLiveStackDay = {
  day: string;
  n: number;
  wins?: number;
  wr?: number | null;
  win_rate_pct?: number | null;
  sum_net?: number;
  sum_pct?: number;
  mean_net?: number | null;
  avg_net_pct?: number | null;
  worst_pct?: number | null;
  mdd?: number | null;
  realized_eq_pct?: number | null;
  worst_eq_pct?: number | null;
  has_data?: boolean;
};

export type BithumbLiveStack = {
  stack_id?: string;
  title?: string;
  note?: string;
  rule?: Record<string, unknown>;
  days: BithumbLiveStackDay[];
  span?: {
    n: number;
    wins?: number;
    wr?: number | null;
    win_rate_pct?: number | null;
    sum_net?: number;
    sum_pct?: number;
    mean_net?: number | null;
    avg_net_pct?: number | null;
    worst_pct?: number | null;
    mdd?: number | null;
    realized_eq_pct?: number | null;
    worst_eq_pct?: number | null;
    has_data?: boolean;
    from?: string;
    to?: string;
  } | null;
};

/** Like Binance catalyst_seed — 성적 button blinks when not ok. */
export type BithumbAllowlistSeed = {
  status?: string | null;
  ran_at?: string | null;
  ok_at?: string | null;
  detail?: string | null;
  core?: string[];
  sat?: string[];
  updated_at?: string | null;
};

export type BithumbPaperSnapshot = {
  strategy: string;
  thesis_wr: number;
  started_at: string;
  updated_at: string;
  config: Record<string, unknown>;
  cluster?: BithumbClusterInfo | null;
  watch?: BithumbWatchRow[];
  open: BithumbPaperTrade[];
  closed: BithumbPaperTrade[];
  live_stack?: BithumbLiveStack | null;
  allowlist_seed?: BithumbAllowlistSeed | null;
  summary: {
    n: number;
    wins: number;
    wr: number | null;
    sum_net: number;
    mean_net: number | null;
    days_live: number;
    wr_target?: number;
    n_watch?: number;
    n_ready?: number;
    n_allow?: number;
  };
  last_error?: string | null;
  ticks_seen?: number;
};

const BASE = '/mercury-ibkr/bithumb-paper';

export async function fetchBithumbMidnightPaper(): Promise<BithumbPaperSnapshot> {
  const res = await fetch(BASE, { cache: 'no-store' });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(`bithumb-paper ${res.status}${t ? `: ${t.slice(0, 120)}` : ''}`);
  }
  return res.json() as Promise<BithumbPaperSnapshot>;
}
